import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import { faqData } from "../pages/FaqData";

function FAQPage() {
    const categories = [
        "All",
        "About RentGurus",
        "Booking",
        "Pricing & Payment",
        "Modifications & Cancellations",
        "Driver Requirements",
        "Insurance & Protection",
        "Pick-up & Drop-off",
        "Fuel, Mileage & Usage Policies",
        "Extras, Support & After-Rental"
    ];

    const [activeCategory, setActiveCategory] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [openIndexes, setOpenIndexes] = useState({});
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsMobileMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredFaqs = faqData.filter((faq) => {
        const matchesCategory = activeCategory === "All" || faq.category === activeCategory;
        const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const toggleAccordion = (index) => {
        setOpenIndexes((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    return (
        <div className="min-h-screen bg-surface py-12 md:py-20 font-body text-text selection:bg-primary/10">
            <div className="max-w-7xl mx-auto px-6">

                {/* Left Side Content Header */}
                <div className="mb-12 max-w-3xl">
                    <Link to="/" className="inline-flex items-center gap-1 text-[13px] font-medium text-gray-600 hover:text-black transition-colors mb-4 font-heading tracking-wide">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 transform rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        Back to Home
                    </Link>
                    
                    {/* Updated to Badge Styling */}
                    <div className="mb-3">
                        <span className="inline-block bg-primary/10 text-primary font-bold tracking-widest uppercase text-[10px] md:text-[11px] font-heading px-3 py-1 rounded-full">
                            Support Center
                        </span>
                    </div>

                    <h1 className="text-[28px] md:text-[40px] font-bold tracking-tight text-dark leading-[1.15] mb-4 font-heading">
                        Frequently asked <span className="text-primary">questions</span>
                    </h1>
                    <p className="text-[14px] md:text-[15px] text-text-muted font-normal leading-relaxed">
                        Find answers to common inquiries regarding bookings, documentation, and operational policies. Can't find what you need? Our team is always one click away.
                    </p>
                </div>

                {/* Live Search Input Bar */}
                <div className="relative mb-8 max-w-2xl">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-light w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search questions, rules, insurance keywords..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-surface-raised border border-surface-border rounded-xl py-3 pl-11 pr-4 text-[14px] text-text placeholder-text-light focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-inner"
                    />
                </div>

                {/* Mobile Dropdown Menu Accordion Trigger */}
                <div className="block md:hidden mb-6 relative" ref={dropdownRef}>
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="w-full bg-surface border border-surface-border rounded-xl px-4 py-3.5 flex justify-between items-center text-text focus:outline-none border-l-4 border-l-primary"
                    >
                        <div className="flex items-center gap-2">
                            <Filter size={14} className="text-dark" />
                            <span className="font-bold text-[12px] uppercase tracking-wider text-dark font-heading">
                                Filter: {activeCategory}
                            </span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 text-text-light transition-transform duration-200 ${isMobileMenuOpen ? "rotate-180 text-dark" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    <div className={`absolute left-0 right-0 z-50 mt-2 bg-surface border border-surface-border rounded-xl shadow-xl transition-all duration-300 ease-in-out origin-top overflow-hidden ${isMobileMenuOpen ? "opacity-100 scale-100 max-h-[320px] overflow-y-auto" : "opacity-0 scale-95 max-h-0 pointer-events-none"}`}>
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => {
                                    setActiveCategory(category);
                                    setOpenIndexes({});
                                    setIsMobileMenuOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wider border-b border-surface-raised last:border-0 transition-colors duration-150 font-heading ${activeCategory === category
                                        ? "bg-dark text-surface"
                                        : "text-text-muted hover:bg-surface-raised hover:text-primary"
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content Layout Block */}
                <div className="grid grid-cols-1 md:grid-cols-10 gap-8 items-start">

                    {/* Left Sidebar Category Filter Tree Menu (Desktop) */}
                    <div className="hidden md:block md:col-span-3 bg-surface border border-surface-border rounded-xl overflow-hidden shadow-sm sticky top-6">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => {
                                    setActiveCategory(category);
                                    setOpenIndexes({});
                                }}
                                className={`w-full text-left px-4 py-3.5 font-bold text-[11px] md:text-[12px] uppercase tracking-wider transition-all duration-150 border-b border-surface-raised last:border-0 block border-l-4 font-heading ${activeCategory === category
                                        ? "bg-surface-raised text-dark border-l-primary"
                                        : "text-text-muted border-l-transparent hover:bg-surface-raised hover:text-dark"
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Right Accordion Layout Columns */}
                    <div className="md:col-span-7 flex flex-col">
                        {filteredFaqs.length > 0 ? (
                            filteredFaqs.map((faq, index) => {
                                const isOpen = !!openIndexes[index];
                                return (
                                    <div key={index} className="border-b border-surface-border">
                                        <button
                                            onClick={() => toggleAccordion(index)}
                                            className="w-full flex justify-between items-center py-4 text-left group transition-colors duration-150"
                                        >
                                            <div className="flex flex-col pr-4">
                                                {activeCategory === "All" && (
                                                    <span className="text-[10px] uppercase tracking-widest text-text-light font-extrabold mb-0.5 font-heading">
                                                        {faq.category}
                                                    </span>
                                                )}
                                                <span className="text-[14px] md:text-[15px] font-semibold text-dark tracking-wide group-hover:text-primary transition-colors font-heading">
                                                    {faq.question}
                                                </span>
                                            </div>
                                            
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className={`h-3.5 w-3.5 text-text-light transition-transform duration-200 flex-shrink-0 group-hover:text-primary ${isOpen ? "rotate-180" : ""}`}
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        <div
                                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                                isOpen ? "max-h-60 pb-5 opacity-100" : "max-h-0 opacity-0"
                                            }`}
                                        >
                                            <p className="text-[13px] md:text-[14px] text-text-muted font-normal leading-relaxed whitespace-pre-line">
                                                {faq.answer}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-16 bg-surface-raised rounded-xl border border-dashed border-surface-border">
                                <p className="text-text-muted text-[14px] font-medium">No questions found matching your selection.</p>
                            </div>
                        )}
                    </div>

                </div>

            </div>
        </div>
    );
}

export default FAQPage;